import React, {useCallback, useMemo} from 'react';
import {StepHeader} from './StepHeader';
import {DynamicStep} from './DynamicStep';
import {useControllerProps} from '../../../Widget/ControllerContext';
import {StepState} from '../../../../../types/checkoutApp.types';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../../../common/constants';

export interface StepProps {
  index: number;
  title: string;
  dataHook: string;
  classNameDynamicStep?: string;
  classNameStepHeader?: string;
  collapsedComponent?: () => JSX.Element;
  openComponent: (props: any) => JSX.Element;
  emptyComponent?: () => JSX.Element;
}

export enum StepDataHooks {
  header = 'StepDataHooks.header',
}

export const Step = ({
  index,
  title,
  dataHook,
  classNameDynamicStep,
  classNameStepHeader,
  openComponent: OpenComponent,
  collapsedComponent: CollapsedComponent,
  emptyComponent: EmptyComponent,
}: StepProps) => {
  const {
    stepsManagerStore: {stepsList},
  } = useControllerProps();
  const {experiments} = useExperiments();

  const stepState = stepsList[index].state;

  const shouldRenderStepContentWithCallback = experiments.enabled(SPECS.ShouldRenderStepContentWithCallback);

  const StepInternalOld = useMemo(
    () =>
      /* eslint-disable-next-line react/display-name */
      () => {
        return (
          <>
            <StepHeader index={index} label={title} dataHook={StepDataHooks.header} className={classNameStepHeader} />
            {/* istanbul ignore next */ stepState === StepState.EMPTY && EmptyComponent && <EmptyComponent />}
            {stepState === StepState.OPEN && <OpenComponent />}
            {stepState === StepState.COLLAPSED && CollapsedComponent && <CollapsedComponent />}
          </>
        );
      },
    [EmptyComponent, CollapsedComponent, OpenComponent, title, index, stepState, classNameStepHeader]
  );

  const StepInternal = useCallback(() => {
    return (
      <>
        <StepHeader index={index} label={title} dataHook={StepDataHooks.header} className={classNameStepHeader} />
        {/* istanbul ignore next */ stepState === StepState.EMPTY && EmptyComponent && <EmptyComponent />}
        {stepState === StepState.OPEN && <OpenComponent />}
        {stepState === StepState.COLLAPSED && CollapsedComponent && <CollapsedComponent />}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, index, stepState, classNameStepHeader]);

  return (
    <DynamicStep
      index={index}
      dataHook={dataHook}
      component={shouldRenderStepContentWithCallback ? StepInternal : StepInternalOld}
      className={classNameDynamicStep}
    />
  );
};
